import { organizationApi, type Member, type OrgRole } from '@shared/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Users } from 'lucide-react';
import * as React from 'react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/ui/empty-state';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useFormError } from '@/hooks/use-form-errors';
import { useSession } from '@/hooks/use-session';
import { ConfirmDialog } from '../components/confirm-dialog';
import { SettingsSection } from '../components/settings-section';

const ROLE_RANK: Record<OrgRole, number> = {
  viewer: 0,
  member: 1,
  admin: 2,
  owner: 3,
};

const ROLE_DESCRIPTIONS: Record<OrgRole, string> = {
  owner: 'Full access, including billing and deleting the organization',
  admin: 'Full access, including members and integrations',
  member: 'Read findings and create API keys',
  viewer: 'Read-only',
};

const STATUS_TONE = {
  active: 'success',
  pending: 'warning',
  inactive: 'neutral',
  declined: 'critical',
} as const;

const MemberRow = ({
  member,
  callerRole,
  isSelf,
  onChangeRole,
  onRemove,
}: {
  member: Member;
  callerRole: OrgRole;
  isSelf: boolean;
  onChangeRole: (role: OrgRole) => void;
  onRemove: () => void;
}) => {
  /**
   * Mirrors the server's rules so the UI does not offer actions that will be
   * refused: you cannot change your own role, and you must outrank the person
   * you are editing.
   */
  const canManage =
    !isSelf &&
    ROLE_RANK[callerRole] >= ROLE_RANK.admin &&
    ROLE_RANK[callerRole] > ROLE_RANK[member.role];

  const assignableRoles = (Object.keys(ROLE_RANK) as OrgRole[]).filter(
    (role) => ROLE_RANK[role] <= ROLE_RANK[callerRole],
  );

  return (
    <li className="flex items-center gap-3 px-5 py-3.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-ink">
          {member.name || member.email}
          {isSelf ? (
            <span className="ml-1.5 text-xs font-normal text-ink-subtle">
              you
            </span>
          ) : null}
        </p>
        {member.name ? (
          <p className="truncate text-sm text-ink-muted">{member.email}</p>
        ) : null}
      </div>

      {member.status !== 'active' ? (
        <Badge tone={STATUS_TONE[member.status]}>{member.status}</Badge>
      ) : null}

      <Badge tone={member.role === 'owner' ? 'accent' : 'neutral'}>
        {member.role}
      </Badge>

      {canManage || isSelf ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Member actions">
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end">
            {canManage ? (
              <>
                <DropdownMenuLabel>Change role</DropdownMenuLabel>
                {assignableRoles.map((role) => (
                  <DropdownMenuItem
                    key={role}
                    disabled={role === member.role}
                    onSelect={() => onChangeRole(role)}
                  >
                    <span className="flex-1">{role}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
              </>
            ) : null}

            <DropdownMenuItem destructive onSelect={onRemove}>
              {isSelf ? 'Leave organization' : 'Remove from organization'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <span className="size-9" aria-hidden />
      )}
    </li>
  );
};

const InviteSection = ({ callerRole }: { callerRole: OrgRole }) => {
  const queryClient = useQueryClient();
  const { error, captureError, clearError } = useFormError();
  const [role, setRole] = React.useState<OrgRole>('member');
  const [invited, setInvited] = React.useState<string | null>(null);
  const formRef = React.useRef<HTMLFormElement>(null);

  const mutation = useMutation({
    mutationFn: organizationApi.inviteMember,
    onSuccess: (member) => {
      formRef.current?.reset();
      setInvited(member.email);
      queryClient.invalidateQueries({ queryKey: ['members'] });
    },
    onError: captureError,
  });

  const assignableRoles = (Object.keys(ROLE_RANK) as OrgRole[]).filter(
    (candidate) => ROLE_RANK[candidate] <= ROLE_RANK[callerRole],
  );

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearError();
    setInvited(null);

    mutation.mutate({
      email: String(new FormData(event.currentTarget).get('email')).trim(),
      role,
    });
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit}>
      <SettingsSection
        title="Invite a teammate"
        description="They get access to this organization's findings and projects."
        footer={
          <Button type="submit" loading={mutation.isPending}>
            Send invitation
          </Button>
        }
      >
        <div className="space-y-4">
          {error ? <Alert tone="error">{error}</Alert> : null}
          {invited ? (
            <Alert tone="success">
              Invited {invited}. They appear below as pending until they accept.
            </Alert>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-[1fr_10rem]">
            <Field label="Email" htmlFor="invite-email">
              <Input
                name="email"
                type="email"
                required
                placeholder="teammate@company.com"
              />
            </Field>

            <Field
              label="Role"
              htmlFor="invite-role"
              hint={ROLE_DESCRIPTIONS[role]}
            >
              <Select
                value={role}
                onValueChange={(next) => setRole(next as OrgRole)}
              >
                <SelectTrigger id="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {assignableRoles.map((candidate) => (
                    <SelectItem key={candidate} value={candidate}>
                      {candidate}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </div>
      </SettingsSection>
    </form>
  );
};

export const MembersTab = () => {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const { error, captureError, clearError } = useFormError();
  const [pendingRemoval, setPendingRemoval] = React.useState<Member | null>(
    null,
  );

  const callerRole = (user?.currentOrganization.role ?? 'viewer') as OrgRole;
  const canSeeMembers = ROLE_RANK[callerRole] >= ROLE_RANK.admin;

  const membersQuery = useQuery({
    queryKey: ['members'],
    queryFn: organizationApi.listMembers,
    // The API refuses this for members and viewers, so do not ask.
    enabled: canSeeMembers,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['members'] });

  const roleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: OrgRole }) =>
      organizationApi.updateMemberRole(userId, { role }),
    onSuccess: invalidate,
    onError: captureError,
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => organizationApi.removeMember(userId),
    onSuccess: () => {
      setPendingRemoval(null);
      invalidate();
    },
    onError: (err) => {
      setPendingRemoval(null);
      captureError(err);
    },
  });

  if (!canSeeMembers) {
    return (
      <Alert tone="info">
        Only admins and owners can see and manage the member list.
      </Alert>
    );
  }

  const members = membersQuery.data?.members ?? [];

  return (
    <div className="space-y-6">
      <InviteSection callerRole={callerRole} />

      {error ? <Alert tone="error">{error}</Alert> : null}

      <div className="overflow-hidden rounded-card border border-border bg-surface">
        <div className="flex items-baseline justify-between gap-3 border-b border-border px-5 py-4">
          <h2 className="text-sm font-semibold tracking-tight text-ink">
            Members
          </h2>
          <span className="text-xs text-ink-subtle">
            {members.length} {members.length === 1 ? 'person' : 'people'}
          </span>
        </div>

        {membersQuery.isLoading ? (
          <div className="space-y-3 p-5">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : members.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No members yet"
            description="Invite a teammate above to share findings with them."
          />
        ) : (
          <ul className="divide-y divide-border">
            {members.map((member) => (
              <MemberRow
                key={member.userId}
                member={member}
                callerRole={callerRole}
                isSelf={member.userId === user?.id}
                onChangeRole={(role) => {
                  clearError();
                  roleMutation.mutate({ userId: member.userId, role });
                }}
                onRemove={() => {
                  clearError();
                  setPendingRemoval(member);
                }}
              />
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pendingRemoval)}
        onOpenChange={(open) => !open && setPendingRemoval(null)}
        title={
          pendingRemoval?.userId === user?.id
            ? 'Leave this organization?'
            : `Remove ${pendingRemoval?.name || pendingRemoval?.email}?`
        }
        description={
          pendingRemoval?.userId === user?.id
            ? 'You lose access to this organization immediately. An admin has to invite you back.'
            : 'They lose access immediately. Any API keys they created keep working.'
        }
        confirmLabel={
          pendingRemoval?.userId === user?.id ? 'Leave' : 'Remove member'
        }
        loading={removeMutation.isPending}
        onConfirm={() => {
          if (pendingRemoval) {
            removeMutation.mutate(pendingRemoval.userId);
          }
        }}
      />
    </div>
  );
};
